import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, catchError, of, switchMap } from 'rxjs';
import { MsalInterceptorConfiguration, MsalService, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { BrowserConfigurationAuthError, InteractionType, StringUtils, UrlString } from '@azure/msal-browser';

function getScopesForEndpoint(
    endpoint: string,
    httpMethod: string,
    location: Location,
    config: MsalInterceptorConfiguration
): string[] | null {
    const normalizedEndpoint = location.normalize(endpoint);
    const protectedResourcesArray = Array.from(config.protectedResourceMap.keys());
    const matchingProtectedResources = matchResourcesToEndpoint(protectedResourcesArray, normalizedEndpoint, location);
    if (matchingProtectedResources.length > 0) {
        return matchScopesToEndpoint(config.protectedResourceMap, matchingProtectedResources, httpMethod);
    }
    return null;
}

function matchResourcesToEndpoint(
    protectedResourcesEndpoints: string[],
    endpoint: string,
    location: Location
): string[] {
    return protectedResourcesEndpoints.filter((key) => {
        const normalizedKey = location.normalize(key);
        const keyComponents = new UrlString(key).getUrlComponents();
        const relativeNormalizedKey = keyComponents.QueryString
            ? keyComponents.AbsolutePath + '?' + keyComponents.QueryString
            : location.normalize(keyComponents.AbsolutePath);

        if (relativeNormalizedKey === '' || relativeNormalizedKey === '/*') {
            return StringUtils.matchPattern(normalizedKey, endpoint);
        }
        return StringUtils.matchPattern(normalizedKey, endpoint) || StringUtils.matchPattern(relativeNormalizedKey, endpoint);
    });
}

function matchScopesToEndpoint(
    protectedResourceMap: Map<string, any>,
    endpointArray: string[],
    httpMethod: string
): string[] | null {
    const allMatchedScopes: (string[] | null)[] = [];

    endpointArray.forEach((matchedEndpoint) => {
        const scopesForEndpoint: string[] = [];
        const methodAndScopesArray = protectedResourceMap.get(matchedEndpoint);

        if (methodAndScopesArray === null) {
            allMatchedScopes.push(null);
            return;
        }

        methodAndScopesArray.forEach((entry: any) => {
            if (typeof entry === 'string') {
                scopesForEndpoint.push(entry);
            } else {
                const normalizedRequestMethod = httpMethod.toLowerCase();
                const normalizedResourceMethod = entry.httpMethod.toLowerCase();
                if (normalizedResourceMethod === normalizedRequestMethod) {
                    entry.scopes.forEach((scope: string) => scopesForEndpoint.push(scope));
                }
            }
        });

        if (scopesForEndpoint.length > 0) {
            allMatchedScopes.push(scopesForEndpoint);
        }
    });

    return allMatchedScopes.length > 0 ? allMatchedScopes[0] : null;
}

function idTokenExpired(expiryTime: number | undefined): boolean {
    if (expiryTime === undefined) return false;
    const exp = expiryTime * 1000;
    return new Date(exp) <= new Date();
}

export const diaHttpInterceptor: HttpInterceptorFn = (req, next) => {
    const msalService = inject(MsalService);
    const location = inject(Location);
    const config = inject<MsalInterceptorConfiguration>(MSAL_INTERCEPTOR_CONFIG);

    if (
        config.interactionType !== InteractionType.Popup &&
        config.interactionType !== InteractionType.Redirect
    ) {
        throw new BrowserConfigurationAuthError(
            'invalid_interaction_type',
            'Invalid interaction type provided to MSAL Interceptor.'
        );
    }

    const scopes = getScopesForEndpoint(req.url, req.method, location, config);

    if (!scopes || scopes.length === 0) {
        return next(req);
    }

    const account = msalService.instance.getActiveAccount() ?? msalService.instance.getAllAccounts()[0];
    const authRequest =
        typeof config.authRequest === 'function'
            ? config.authRequest(msalService, req, { account })
            : { ...config.authRequest, account };

    const isForceRefresh = idTokenExpired(account?.idTokenClaims?.exp);

    return msalService.acquireTokenSilent({ ...authRequest, scopes, account, forceRefresh: isForceRefresh }).pipe(
        catchError(() => msalService.acquireTokenPopup({ ...authRequest, scopes })),
        switchMap((result) => (!result.idToken ? msalService.acquireTokenPopup({ ...authRequest, scopes }) : of(result))),
        switchMap((result) => {
            const headers = req.headers.set('Authorization', 'Bearer ' + result.idToken);
            return next(req.clone({ headers }));
        })
    ) as Observable<any>;
};